import React, { useMemo, useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { deleteWorkTypeLabour } from "../../helpers/estimates";
import {
  computeLabourLineTotal,
  computeWorkTypeLabourTotal,
  formatCurrency,
  formatLabourLineDetail,
} from "../../helpers/estimateTotals";
import { EstimateOverflowMenu } from "./EstimateOverflowMenu";
import { LabourFormSheet } from "./LabourFormSheet";

/**
 * Compact Labour section — same visual language as Measurements/Materials.
 * Reuses addWorkTypeLabour (via LabourFormSheet) / deleteWorkTypeLabour
 * unchanged. Labour delete stays a hard delete — not normalized here.
 */
export function FeatureLabourSection({ workType, materialsCatalog, editable, onReload }) {
  const { actor } = usePermissions();
  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const labourRows = workType.labour || [];
  const labourTotal = useMemo(
    () => computeWorkTypeLabourTotal(workType),
    [workType],
  );

  async function handleDelete(row) {
    if (!editable) return;
    if (!window.confirm(`Remove "${row.labour_activity}"?`)) return;
    try {
      await deleteWorkTypeLabour(actor, row.labour_id);
      await onReload?.();
    } catch (err) {
      console.error("[FeatureLabourSection] Delete failed:", err);
      alert(err.message || "Unable to remove labour.");
    }
  }

  return (
    <div className="est-section-block">
      <div className="est-section-summary-row">
        <span className="est-section-summary-label">Labour</span>
        {labourTotal > 0 && (
          <span className="est-section-summary-value">{formatCurrency(labourTotal)}</span>
        )}
      </div>

      {labourRows.length === 0 ? (
        <div className="est-home-empty-row">No labour yet.</div>
      ) : (
        labourRows.map((row) => (
          <div
            key={row.labour_id}
            className="est-row"
            role={editable ? "button" : undefined}
            tabIndex={editable ? 0 : undefined}
            onClick={() => editable && setEditingRow(row)}
          >
            <div className="est-row-main">
              <div className="est-row-title">{row.labour_activity}</div>
              <div className="est-row-meta">{formatLabourLineDetail(row, workType)}</div>
            </div>
            <div className="est-row-value">
              {formatCurrency(computeLabourLineTotal(row, workType))}
            </div>
            {editable && (
              <EstimateOverflowMenu
                ariaLabel="Labour actions"
                items={[
                  { label: "Remove", onClick: () => handleDelete(row), destructive: true },
                ]}
              />
            )}
          </div>
        ))
      )}

      {editable && (
        <button type="button" className="est-add-row-btn" onClick={() => setShowAdd(true)}>
          + Add Labour
        </button>
      )}

      {showAdd && (
        <LabourFormSheet
          workType={workType}
          materialsCatalog={materialsCatalog}
          onClose={() => setShowAdd(false)}
          onSaved={onReload}
        />
      )}

      {editingRow && (
        <LabourFormSheet
          workType={workType}
          materialsCatalog={materialsCatalog}
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSaved={onReload}
        />
      )}
    </div>
  );
}
